import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Autocomplete,
} from "@mui/material";
import { Coach, CreateCoachData, UpdateCoachData } from "../types/Coach";
import { Team, teamsApi } from "../services/teamsApi";
import { coachesApi } from "../services/coachesApi";
import { toast } from "react-toastify";
import { se } from "date-fns/locale";

interface CoachFormProps {
  open: boolean;
  onClose: () => void;
  coach?: Coach | null;
  onSuccess: () => void;
}

const CoachForm: React.FC<CoachFormProps> = ({
  open,
  onClose,
  coach,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    mail: "",
    number: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Team-related state
  const [teamSearchInput, setTeamSearchInput] = useState("");
  const [teamOptions, setTeamOptions] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamSearchLoading, setTeamSearchLoading] = useState(false);

  const isEdit = !!coach;

  useEffect(() => {
    if (coach) {
      setFormData({
        name: coach.name,
        mail: coach.mail,
        number: coach.number,
      });
      // Set selected team if coach has a team
      if (coach.team) {
        setSelectedTeam(coach.team);
      }
    } else {
      setFormData({
        name: "",
        mail: "",
        number: "",
      });
      setSelectedTeam(null);
    }
    setError("");
  }, [coach, open]);

  // Team search effect
  useEffect(() => {
    const searchTeams = async () => {
      if (teamSearchInput.length >= 2) {
        setTeamSearchLoading(true);
        try {
          const response = await teamsApi.search(teamSearchInput, 10);
          setTeamOptions(response.data.teams);
        } catch (error) {
          console.error("Error searching teams:", error);
          setTeamOptions([]);
        } finally {
          setTeamSearchLoading(false);
        }
      } else {
        setTeamOptions([]);
      }
    };

    const debounceTimer = setTimeout(searchTeams, 300);
    return () => clearTimeout(debounceTimer);
  }, [teamSearchInput]);

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const validateForm = () => {
    if (!formData.name?.trim()) {
      setError("Le nom est requis");
      return false;
    }
    // if (!formData.mail.trim()) {
    //   setError("L'email est requis");
    //   return false;
    // }
    if (!formData.number?.trim()) {
      setError("Le numéro de téléphone est requis");
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.mail?.trim() && !emailRegex.test(formData.mail)) {
      setError("Veuillez entrer un email valide");
      return false;
    }

    // Phone number format validation
    if (!formData.number.startsWith("+")) {
      setError(
        "Le format du numéro doit être +33 ou avoir un + dans le numéro pour être ok"
      );
      toast.error(
        "Le format du numéro doit être +33 ou avoir un + dans le numéro pour être ok"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isEdit && coach) {
        const updateData: UpdateCoachData = {};
        if (formData.name !== coach.name) updateData.name = formData.name;
        if (formData.mail !== coach.mail) updateData.mail = formData.mail;
        if (formData.number !== coach.number)
          updateData.number = formData.number;
        if (selectedTeam && selectedTeam._id !== coach.team?._id) {
          updateData.teamId = selectedTeam._id;
        }
        if (!selectedTeam) {
          updateData.teamId = undefined;
        }

        if (Object.keys(updateData).length > 0) {
          await coachesApi.update(coach._id, updateData);
        }
        toast.success("Entraîneur mis à jour avec succès");
      } else {
        if (selectedTeam) {
          try {
            const createData: CreateCoachData = {
              name: formData.name,
              mail: formData.mail,
              number: formData.number,
              teamId: selectedTeam._id,
            };
            await coachesApi.create(createData);
            // coachId = newCoach._id;
            toast.success("Entraîneur créé avec succès");
            // await coachesApi.linkTeam(coachId, selectedTeam._id);
            toast.success("Équipe liée avec succès");
          } catch (teamLinkError) {
            console.error("Error linking team:", teamLinkError);
            toast.warning(
              "Entraîneur sauvegardé mais erreur lors de la liaison de l'équipe"
            );
          }
        } else {
          try {
            // No team selected, just create the coach without linking
            const createData: CreateCoachData = {
              name: formData.name,
              mail: formData.mail,
              number: formData.number,
            };
            await coachesApi.create(createData);
            toast.success("Entraîneur créé avec succès");
          } catch (createError) {
            console.error("Error creating coach:", createError);
            toast.error("Erreur lors de la création de l'entraîneur");
          }
        }
      }

      // Link team if one is selected

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error saving coach:", err);
      setError(
        err.response?.data?.message ||
          "Une erreur est survenue lors de la sauvegarde"
      );
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Modifier l'entraîneur" : "Nouvel entraîneur"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nom complet"
              value={formData.name}
              onChange={handleChange("name")}
              required
              variant="outlined"
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.mail}
              onChange={handleChange("mail")}
              // required
              variant="outlined"
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Numéro de téléphone format +33"
              value={formData.number}
              onChange={handleChange("number")}
              required
              variant="outlined"
              disabled={loading}
              placeholder="+33123456789"
            />

            <Autocomplete
              options={teamOptions}
              value={selectedTeam}
              onChange={(event, newValue) => {
                setSelectedTeam(newValue);
              }}
              inputValue={teamSearchInput}
              onInputChange={(event, newInputValue) => {
                setTeamSearchInput(newInputValue);
              }}
              getOptionLabel={(option) =>
                `${option.name} (${option.shortName})`
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              loading={teamSearchLoading}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Équipe"
                  placeholder="Rechercher une équipe..."
                  helperText="Tapez au moins 2 caractères pour rechercher"
                />
              )}
              noOptionsText={
                teamSearchInput.length < 2
                  ? "Tapez au moins 2 caractères"
                  : "Aucune équipe trouvée"
              }
              sx={{
                "& .MuiChip-root": {
                  margin: "2px",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleClose} disabled={loading} color="inherit">
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
              },
            }}
          >
            {loading ? "Sauvegarde..." : isEdit ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CoachForm;
